import { useCallback } from 'react';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';
import { UiPersistence } from '~/types/angular.types';
import { DeepValue, Paths } from '~/types/app.types';

function useUiPersistedState<T extends Paths<UiPersistence>>(
    key: T,
    defaultValue: Partial<DeepValue<UiPersistence, T>>
) {
    const { uiPersistenceStore } = useGlobalMX();

    type U = DeepValue<UiPersistence, T>;
    const getValue = useCallback(
        () => uiPersistenceStore.get(key, defaultValue),
        // defaultValue is omitted from the dependency array because it should be thought of
        // like a useState initial value, we only care about it in the first render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [key, uiPersistenceStore]
    );

    const state = getValue();

    const setValue = useCallback(
        (
            newValue:
                | Partial<U>
                | ((prev: Partial<NonNullable<U>>) => Partial<U>)
        ) => {
            const _newValue =
                newValue instanceof Function ? newValue(getValue()) : newValue;
            uiPersistenceStore.set(key, _newValue);
        },
        [getValue, key, uiPersistenceStore]
    );

    return [state!, setValue] as const;
}

export default useUiPersistedState;
